// «حصريات معبر» — products a Maabar team member sells through an ordinary
// supplier account (owner, 2026-10-29). The store shows them on their own page
// (/market/exclusives), apart from the HioBuy catalogue and its cart: the
// trader opens a chat with the team member, negotiates everything (price,
// quantity, shipping) with auto-translation, and pays the chat invoice the
// team member issues — in SAR, with no Maabar fee (issue_chat_invoice).
//
// Who is an exclusive seller is decided by the admin (admin_set_exclusive_seller,
// market_exclusive_sellers); is_exclusive_seller() is the one predicate, and
// market_exclusive_products is the read-only list the store shows.

import { sb } from '../supabase';
import { buildProductRef } from './factoryChat';
import { getProductDisplayImages } from './productMedia';
import { detectSourceLang, translateText } from './aiTranslate';

export const EXCLUSIVES_PAGE = 24;
const COLS = '*';

/** One page of exclusive products, newest first; one category when categoryId is set. */
export async function fetchExclusives({ limit = EXCLUSIVES_PAGE, offset = 0, categoryId = null } = {}) {
  let q = sb.from('market_exclusive_products').select(COLS, { count: 'exact' });
  // A product can be in several categories (category_ids, 2026-10-29).
  if (categoryId) q = q.contains('category_ids', [categoryId]);
  const { data, error, count } = await q
    .order('created_at', { ascending: false })
    .range(offset, offset + limit - 1);
  if (error) { console.error('[exclusives] list failed:', error.message); return { rows: [], total: 0, error: error.message }; }
  return { rows: data || [], total: count ?? (data || []).length, error: null };
}

/** One exclusive product, or null when it is not (or no longer) on the exclusives page. */
export async function fetchExclusive(id) {
  if (!id) return null;
  const { data, error } = await sb.from('market_exclusive_products').select(COLS).eq('id', id).maybeSingle();
  if (error) { console.error('[exclusives] product failed:', error.message); return null; }
  return data || null;
}

/** Is this supplier account a «حصريات معبر» seller? false on any error (never blocks a page). */
export async function isExclusiveSeller(supplierId) {
  if (!supplierId) return false;
  const { data, error } = await sb.rpc('is_exclusive_seller', { p_supplier_id: supplierId });
  if (error) { console.error('[exclusives] is_exclusive_seller failed:', error.message); return false; }
  return data === true;
}

/** Is the signed-in account a «حصريات معبر» seller? */
export async function amIExclusiveSeller() {
  const { data: { user } = {} } = await sb.auth.getUser();
  return user ? isExclusiveSeller(user.id) : false;
}

export const exclusiveImages = (p) => getProductDisplayImages(p);
export const exclusiveImage = (p) => exclusiveImages(p)[0] || null;

export function exclusiveTitle(p, lang) {
  if (!p) return '';
  if (lang === 'ar') return p.name_ar || p.name_en || p.name_zh || '';
  if (lang === 'zh') return p.name_zh || p.name_en || p.name_ar || '';
  return p.name_en || p.name_zh || p.name_ar || '';
}

export function exclusiveDesc(p, lang) {
  if (!p) return '';
  if (lang === 'ar') return p.desc_ar || p.desc_en || p.desc_zh || '';
  if (lang === 'zh') return p.desc_zh || p.desc_en || p.desc_ar || '';
  return p.desc_en || p.desc_zh || p.desc_ar || '';
}

/** The "from" price in SAR (the view converts the lowest product-level tier), or null. */
export function exclusiveFromSar(p) {
  const n = Number(p?.from_price_sar);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/**
 * The product card a chat opened from the exclusives page carries. The trader
 * sees SAR only, so the price on it is the SAR figure, not the supplier's own.
 */
export function exclusiveProductRef(p) {
  const sar = exclusiveFromSar(p);
  const ref = buildProductRef({ ...p, image_url: exclusiveImage(p), price: sar != null ? String(sar) : null, currency: sar != null ? 'SAR' : null });
  return ref && { ...ref, kind: 'exclusive' };
}

/** Admin: every exclusive seller with their numbers. */
export async function adminListExclusiveSellers() {
  const { data, error } = await sb.rpc('admin_exclusive_sellers');
  if (error) return { ok: false, error: error.message, rows: [] };
  return { ok: true, rows: Array.isArray(data) ? data : [] };
}

/** Admin: make a supplier account an exclusive seller, or stop. */
export async function adminSetExclusiveSeller(supplierId, active, note) {
  const { data, error } = await sb.rpc('admin_set_exclusive_seller', { p_supplier_id: supplierId, p_active: !!active, p_note: note || null });
  if (error) return { ok: false, error: error.message };
  return { ok: true, data };
}

/**
 * A phone as typed (05…, 5…, 966…, 00966…, +966…, with spaces or dashes) → the
 * E.164 form profiles.phone holds (+966553980028), or null when it is not one.
 * A bare local number is read as Saudi; any other country needs its code.
 */
export function normalizeSupplierPhone(input) {
  let s = String(input || '').trim().replace(/[\s\-().]/g, '');
  if (!s || /[^\d+]/.test(s)) return null;
  if (s.startsWith('00')) s = `+${s.slice(2)}`;
  if (/^05\d{8}$/.test(s)) return `+966${s.slice(1)}`;
  if (/^5\d{8}$/.test(s)) return `+966${s}`;
  if (/^\d{10,15}$/.test(s)) return `+${s}`;
  if (/^\+\d{8,15}$/.test(s)) return s;
  return null;
}

/**
 * Admin: find a supplier account by email, Maabar supplier ID (MS-…) or phone
 * — most suppliers sign up with a phone and no email (owner, 2026-10-29: more
 * than one «موردنا البطل»). Admins read profiles directly (RLS), the same way
 * the console's supplier page does.
 */
export async function adminFindSupplier(query) {
  const q = String(query || '').trim();
  if (!q) return null;
  const phone = !q.includes('@') && !/^ms-/i.test(q) ? normalizeSupplierPhone(q) : null;
  let req = sb.from('profiles').select('id, full_name, company_name, email, phone, maabar_supplier_id, role, status');
  req = q.includes('@') ? req.ilike('email', q) : phone ? req.eq('phone', phone) : req.ilike('maabar_supplier_id', q);
  const { data, error } = await req.limit(2);
  if (error) { console.error('[exclusives] find supplier failed:', error.message); return null; }
  return (data || []).length === 1 ? data[0] : (data || []).length > 1 ? { ambiguous: true } : null;
}

// ── Categories («فئات الحصريات», owner 2026-10-29) ──────────────────────────
// The exclusive seller makes their own categories, named in any language: the
// one they type is detected and the other two are translated with the chat's
// own translation (aiTranslate), shown to them before saving, and editable.

export const LANGS = ['ar', 'en', 'zh'];

/** A category's name in the reader's language, else whichever it has. */
export function categoryName(c, lang) {
  if (!c) return '';
  return c[`name_${lang}`] || c.name_ar || c.name_en || c.name_zh || '';
}

/** The store's category bar: categories with products on the page, in the seller's order. */
export async function fetchExclusiveCategories() {
  const { data, error } = await sb.from('market_exclusive_categories_public')
    .select('id, supplier_id, name_ar, name_en, name_zh, sort, products')
    .order('sort', { ascending: true }).order('created_at', { ascending: true });
  if (error) { console.error('[exclusives] categories failed:', error.message); return []; }
  return data || [];
}

/**
 * A name typed in any language → { ar, en, zh }: the typed one as is, the other
 * two translated. A failed translation leaves that language empty (the server
 * accepts any one name), never the source text under the wrong language.
 */
export async function translateCategoryName(text) {
  const t = String(text || '').trim();
  const out = { ar: '', en: '', zh: '', source: null, failed: [] };
  if (!t) return out;
  const src = detectSourceLang(t);
  out.source = src;
  out[src] = t;
  await Promise.all(LANGS.filter((l) => l !== src).map(async (l) => {
    const r = await translateText(t, l, src);
    if (r.error || !r.translated || r.translated === t) out.failed.push(l);
    else out[l] = r.translated.slice(0, 60);
  }));
  return out;
}

/** The signed-in seller's own categories (RLS: own rows), in order. */
export async function fetchMyExclusiveCategories() {
  const { data, error } = await sb.from('market_exclusive_categories')
    .select('id, name_ar, name_en, name_zh, sort, created_at')
    .order('sort', { ascending: true }).order('created_at', { ascending: true });
  if (error) { console.error('[exclusives] my categories failed:', error.message); return []; }
  return data || [];
}

/** product_id → [category_id, …] for the seller's own products (RLS: own rows). */
export async function fetchMyProductCategories() {
  const { data, error } = await sb.from('market_exclusive_product_categories').select('product_id, category_id');
  if (error) { console.error('[exclusives] product categories failed:', error.message); return {}; }
  const map = {};
  (data || []).forEach((r) => { (map[r.product_id] = map[r.product_id] || []).push(r.category_id); });
  return map;
}

const rpcResult = ({ data, error }) => (error ? { ok: false, error: error.message } : { ok: true, data });

export const saveExclusiveCategory = ({ id = null, ar, en, zh }) =>
  sb.rpc('exclusive_category_save', { p_id: id, p_name_ar: ar || null, p_name_en: en || null, p_name_zh: zh || null }).then(rpcResult);
export const deleteExclusiveCategory = (id) => sb.rpc('exclusive_category_delete', { p_id: id }).then(rpcResult);
export const reorderExclusiveCategories = (ids) => sb.rpc('exclusive_categories_reorder', { p_ids: ids }).then(rpcResult);
/** The full set of categories a product is in (empty = none). */
export const setExclusiveProductCategories = (productId, categoryIds) =>
  sb.rpc('exclusive_product_set_categories', { p_product_id: productId, p_category_ids: categoryIds || [] }).then(rpcResult);

// ── Hidden until launched (owner 2026-10-29) ─────────────────────────────────
// One switch for the whole section. While it is off, the store views return
// nothing to customers (the server's exclusives_visible_to_me); admins and the
// seller still see the page, with a note that customers do not.

/** Is «حصريات معبر» open to customers? false on error (the safe reading). */
export async function exclusivesPublic() {
  const { data, error } = await sb.rpc('exclusives_public');
  if (error) { console.error('[exclusives] exclusives_public failed:', error.message); return false; }
  return data === true;
}

export const adminSetExclusivesPublic = (on) => sb.rpc('admin_set_exclusives_public', { p_public: !!on }).then(rpcResult);
